import { createContext, useContext } from 'react';
import { observable, action } from 'mobx';
import { PixelAppsQuery_pixelTrackingApps_edges_node } from 'api';

type Required<T> = {
  value: T;
  error: string;
};

interface PixelApp extends PixelAppsQuery_pixelTrackingApps_edges_node {
  id: string;
}
const emptyValue = { error: ' ', value: '' };

class PixelAppForm {
  @observable id = '';
  @observable isSubmitting = false;
  @observable editMode = false;

  @observable domain: Required<string> = {
    value: '',
    error: ' '
  };
  @observable facebookPixelId: Required<string> = {
    value: '',
    error: ' '
  };
  @observable estimatedMonthlyTraffic: Required<string> = {
    value: '',
    error: ' '
  };
  @observable pageUniquePart = 'page_urlpath';
  @observable trafficEnabled = true;
  @observable timezone: Required<string> = {
    value: 'Hawaii',
    error: ''
  };
  @action init = (pixelApp: PixelApp) => {
    this.id = pixelApp.id;
    this.editMode = true;
    this.domain.value = pixelApp.domain;
    this.facebookPixelId.value = pixelApp.facebookPixelId;
    this.timezone.value = pixelApp.timezone;
    this.estimatedMonthlyTraffic.value = String(pixelApp.estimatedMonthlyTraffic);
    this.trafficEnabled = pixelApp.trafficEnabled;
    this.pageUniquePart = pixelApp.pageUniquePart;
  };

  @action reset = () => {
    this.id = '';
    this.isSubmitting = false;
    this.editMode = false;
    this.domain = emptyValue;
    this.facebookPixelId = emptyValue;
    this.estimatedMonthlyTraffic = emptyValue;
    this.pageUniquePart = 'page_urlpath';
    this.trafficEnabled = true;
    this.timezone.value = 'Hawaii';
    this.timezone.error = ' ';
  };

  @action getValuesBeforeCreate = () => {
    this.isSubmitting = true;
    return {
      domain: this.domain.value,
      facebookPixelId: this.facebookPixelId.value,
      estimatedMonthlyTraffic: this.estimatedMonthlyTraffic.value,
      trafficEnabled: this.trafficEnabled,
      timezone: this.timezone.value,
      pageUniquePart: this.pageUniquePart
    };
  };

  @action getValuesBeforeEdit = () => {
    this.isSubmitting = true;
    return {
      id: this.id,
      facebookPixelId: this.facebookPixelId.value,
      estimatedMonthlyTraffic: this.estimatedMonthlyTraffic.value,
      trafficEnabled: this.trafficEnabled,
      timezone: this.timezone.value,
      pageUniquePart: this.pageUniquePart
    };
  };

  @action handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    switch (event.target.name) {
      case 'domain':
      case 'estimatedMonthlyTraffic':
      case 'facebookPixelId':
        this[event.target.name as 'domain' | 'estimatedMonthlyTraffic' | 'facebookPixelId'] = {
          error: ' ',
          value: event.target.value
        };
        break;

      default:
        throw new Error('This field is not supported');
    }
  };

  @action handleTimeZoneChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    this.timezone = {
      error: ' ',
      value: event.target.value as string
    };
  };

  @action handlePageUniquePartChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    this.pageUniquePart = event.target.value as string;
  };

  @action handleTrafficEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.trafficEnabled = event.target.checked;
  };

  @action validateFields = () => {
    let errors = 0;
    if (!this.domain.value) {
      this.domain.error = 'Domain is required';
      errors += 1;
    }
    if (!this.facebookPixelId.value) {
      this.facebookPixelId.error = 'Facebook pixel id is required';
      errors += 1;
    }
    if (!this.estimatedMonthlyTraffic.value) {
      this.estimatedMonthlyTraffic.error = 'Estimated monthly traffic is required';
      errors += 1;
    }
    return errors;
  };
}

export const PixelAppFormContext = createContext(new PixelAppForm());

export const usePixelAppFormStore = () => useContext(PixelAppFormContext);

export default PixelAppForm;
